import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider
} from '@mui/material';
import { Close, Security } from '@mui/icons-material';

const PrivacyModal = ({ open, onClose }) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        p: 3, 
        bgcolor: 'white',
        borderBottom: '1px solid #eee',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Security sx={{ color: '#2563eb', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111' }}>
              Privacy Policy
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mt: 0.25 }}>
              Last updated: January 13, 2026
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#999' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4, bgcolor: 'white' }}>
        
        {/* Overview */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          Overview
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          This Privacy Policy explains how Cryptologics-ai LLC collects, uses, and discloses 
          information about you through its websites and other online products and services 
          (collectively, the "Services") or when you otherwise interact with us.
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          We may change this Privacy Policy from time to time. If we make changes, we will 
          notify you by revising the date at the top of the policy. We encourage you to review 
          the Privacy Policy whenever you access the Services to stay informed about our 
          information practices.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Collection of Information */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          Collection of Information
        </Typography>
        
        <Typography sx={{ fontWeight: 600, color: '#222', mb: 1 }}>
          Information You Provide
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          When you register for the Services, we ask you to provide certain information such as 
          your name and email address and ask you to create a password. We also collect information 
          when you participate in any interactive features of the Services (such as API keys, 
          cryptocurrency exchange digital asset allocations), fill out a form, make a purchase, 
          request customer support, or otherwise communicate with us.
        </Typography>

        <Typography sx={{ fontWeight: 600, color: '#222', mb: 1 }}>
          Information from Other Sources
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          We may obtain information from other sources and combine that with information we collect 
          through our Services. For example, we may collect information about you and your 
          cryptocurrency portfolios from third parties, including cryptocurrency exchange platforms 
          and publicly-available sources.
        </Typography>

        <Typography sx={{ fontWeight: 600, color: '#222', mb: 1 }}>
          Automatically Collected Information
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 1 }}>
          When you access or use our Services, we automatically collect information about you, including:
        </Typography>
        <Box component="ul" sx={{ color: '#444', lineHeight: 2, pl: 3, mb: 3 }}>
          <li><strong>Log Information:</strong> Browser type, access times, pages viewed, IP address, and referring page.</li>
          <li><strong>Device Information:</strong> Hardware model, operating system, unique device identifiers, and mobile network information.</li>
          <li><strong>Cookies:</strong> Small data files that help us improve our Services, see popular features, and count visits.</li>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Use of Information */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          Use of Information
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 1 }}>
          We use the information we collect to provide, maintain, and improve our services. We may also use the information to:
        </Typography>
        <Box component="ul" sx={{ color: '#444', lineHeight: 2, pl: 3, mb: 3 }}>
          <li>Send you technical notices, updates, security alerts, and support messages</li>
          <li>Communicate with you about products, services, offers, and events</li>
          <li>Monitor and analyze trends, usage, and activities</li>
          <li>Detect, investigate, and prevent fraudulent transactions and illegal activities</li>
          <li>Personalize and improve the Services</li>
          <li>Comply with legal obligations</li>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Sharing of Information */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          Sharing of Information
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 1 }}>
          We may share information about you in the following circumstances:
        </Typography>
        <Box component="ul" sx={{ color: '#444', lineHeight: 2, pl: 3, mb: 2 }}>
          <li>With vendors and service providers who carry out work on our behalf</li>
          <li>In response to legal requests or to comply with applicable law</li>
          <li>To protect the rights, property, and safety of Cryptologics-ai LLC or others</li>
          <li>With other users of our Services (with your consent)</li>
          <li>In connection with mergers, acquisitions, or sale of company assets</li>
          <li>With your consent or at your direction</li>
        </Box>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          We may also share aggregated or de-identified information, which cannot reasonably be used to identify you.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Data Retention */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          Data Retention & Transfer
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          We store the information we collect for as long as necessary for the purpose(s) for which 
          we originally collected it. We may retain certain information for legitimate business 
          purposes or as required by law.
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Cryptologics-ai LLC is based in the United States and we process and store information 
          in the U.S. Your information may be transferred to, stored, or accessed in jurisdictions 
          that may not provide equivalent levels of data protection as your home jurisdiction.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Your Choices */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          Your Choices
        </Typography>
        
        <Typography sx={{ fontWeight: 600, color: '#222', mb: 1 }}>
          Account Information
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          You may update, correct, or delete information about you at any time by logging into 
          your account or emailing us at support@cryptologics-ai.com. You can remove API keys 
          by logging into your account settings.
        </Typography>

        <Typography sx={{ fontWeight: 600, color: '#222', mb: 1 }}>
          Cookies
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          Most browsers accept cookies by default. You can usually set your browser to remove 
          or reject cookies, though this may affect the functionality of our Services.
        </Typography>

        <Typography sx={{ fontWeight: 600, color: '#222', mb: 1 }}>
          Promotional Communications
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 2 }}>
          You may opt out of promotional emails by following the unsubscribe instructions or 
          emailing support@cryptologics-ai.com. We may still send you non-promotional emails 
          about your account or our ongoing business relations.
        </Typography>

        <Typography sx={{ fontWeight: 600, color: '#222', mb: 1 }}>
          Do Not Track
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8, mb: 3 }}>
          Cryptologics-ai LLC does not respond to DNT signals. For more information, visit donottrack.us.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Contact */}
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111', mb: 1.5 }}>
          Contact Us
        </Typography>
        <Typography sx={{ color: '#444', lineHeight: 1.8 }}>
          If you have any questions about this Privacy Policy, please contact us at{' '}
          <Box component="span" sx={{ color: '#2563eb', fontWeight: 500 }}>
            support@cryptologics-ai.com
          </Box>
        </Typography>

      </DialogContent>

      <DialogActions sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #eee' }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{
            bgcolor: '#2563eb',
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            '&:hover': {
              bgcolor: '#1d4ed8'
            }
          }}
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrivacyModal;